<template>
  <plan-table
    block-id="pricing"
    :kicker="t('Paying less does not mean getting less.')"
    :title="t('Web design plans')"
    :lede="t('A custom website built for your business, with hosting, domain, SSL and email all included. Web subscription plans carry a minimum 12-month contract.')"
    :price-label="t('Price')"
    :price-hint="t('What you pay each month for the plan. The lower rate applies when billed annually.')"
    :columns="columns"
    :rows="rows"
    :remarks="remarks"
  />
</template>

<script>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import PlanTable from '@/Components/PlanTable.vue';

export default {
  components: {
    PlanTable
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          '1 main pages,\nno subpages': '1 main pages,\nno subpages',
          '7 main pages,\nunlimited subpages': '7 main pages,\nunlimited subpages',
          'A custom website built for your business, with hosting, domain, SSL and email all included. Web subscription plans carry a minimum 12-month contract.': 'A custom website built for your business, with hosting, domain, SSL and email all included. Web subscription plans carry a minimum 12-month contract.',
          'Custom plan': 'Custom plan',
          Daily: 'Daily',
          'How many pages your website can have, plus extra pages under them.': 'How many pages your website can have, plus extra pages under them.',
          'or {price} {unit}': 'or {price} {unit}',
          Quote: 'Quote',
          Unlimited: 'Unlimited',
          'What you pay each month for the plan. The lower rate applies when billed annually.': 'What you pay each month for the plan. The lower rate applies when billed annually.',
          'Web design plans': '**Web design** plans',
          'Web subscription plans have a minimum 12-month contract that starts after the project launch.': 'Web subscription plans have a minimum 12-month contract that starts after the project launch.',
          'Build time is about a week for the Startup plan, two to four weeks for the Growth plan, and set out in the project proposal for the Custom plan.': 'Build time is about a week for the Startup plan, two to four weeks for the Growth plan, and set out in the project proposal for the Custom plan.',
          Weekly: 'Weekly',
          'then RM 118 per month': 'then RM 118 per month',
        },
        'zh-MY': {
          '1 main pages,\nno subpages': '1 个主页，\n无子页面',
          '7 main pages,\nunlimited subpages': '7 个主页，\n不限子页面',
          'A custom website built for your business, with hosting, domain, SSL and email all included. Web subscription plans carry a minimum 12-month contract.': '为您的业务定制网站，托管、域名、SSL 与企业邮箱全都包含在内。网页订阅配套在项目上线之后才开始，最少需要签订十二个月的合约，之后可再按年续约。',
          'Custom plan': '客制配套',
          Daily: '每日',
          'How many pages your website can have, plus extra pages under them.': '网站可以有多少个页面，以及它们底下的子页面。',
          'or {price} {unit}': '或 {price} {unit}',
          Quote: '报价',
          Unlimited: '不限',
          'What you pay each month for the plan. The lower rate applies when billed annually.': '每月配套费用。选择按年计费时适用较低的费率。',
          'Web design plans': '**网页设计**配套',
          'Web subscription plans have a minimum 12-month contract that starts after the project launch.': '网页订阅配套在项目上线之后才开始，最短合约为 12 个月。',
          'Build time is about a week for the Startup plan, two to four weeks for the Growth plan, and set out in the project proposal for the Custom plan.': '制作时间：启动配套约一周，成长配套约两至四周，客制配套以项目提案为准。',
          Weekly: '每周',
          'then RM 118 per month': '其后每月 RM 118',
        },
        'ms-MY': {
          '1 main pages,\nno subpages': '1 halaman utama,\ntiada subhalaman',
          '7 main pages,\nunlimited subpages': '7 halaman utama,\nsubhalaman tanpa had',
          'A custom website built for your business, with hosting, domain, SSL and email all included. Web subscription plans carry a minimum 12-month contract.': 'Laman web tersuai untuk perniagaan anda, lengkap dengan hosting, domain, SSL dan e-mel. Langganan terikat minimum dua belas bulan selepas pelancaran projek.',
          'Custom plan': 'Pelan Custom',
          Daily: 'Harian',
          'How many pages your website can have, plus extra pages under them.': 'Berapa banyak halaman laman web anda boleh ada, plus halaman tambahan di bawahnya.',
          'or {price} {unit}': 'atau {price} {unit}',
          Quote: 'Sebut harga',
          Unlimited: 'Tanpa had',
          'What you pay each month for the plan. The lower rate applies when billed annually.': 'Apa yang anda bayar setiap bulan untuk pelan ini. Kadar lebih rendah terpakai apabila dibilkan setiap tahun.',
          'Web design plans': 'Pelan **Reka Bentuk Web**',
          'Web subscription plans have a minimum 12-month contract that starts after the project launch.': 'Pelan langganan web mempunyai kontrak minimum 12 bulan yang bermula selepas pelancaran projek.',
          'Build time is about a week for the Startup plan, two to four weeks for the Growth plan, and set out in the project proposal for the Custom plan.': 'Masa bina lebih kurang seminggu untuk pelan Startup, dua hingga empat minggu untuk pelan Growth, dan ditetapkan dalam cadangan projek untuk pelan Custom.',
          Weekly: 'Mingguan',
          'then RM 118 per month': 'kemudian RM 118 sebulan',
        }
      }
    });

    const yes = { type: 'yes' };
    const no = { type: 'no' };

    /* Both rates ride in the price cell rather than behind a billing switch. The
       annual rate is the headline because it is the one the section sells, and
       the monthly rate is a note under it, so the discount is visible without a
       tap. A plan sold one way only (Custom, which is a quote) carries no note. */
    const columns = computed(() => [
      {
        name: t('Startup plan'),
        audience: t('For new businesses'),
        icon: 'rocket',
        blurb: t('Best for startup businesses and small companies that need to showcase their ideas.'),
        price: 'RM 58',
        unit: t('per month, billed annually'),
        alt: t('or {price} {unit}', { price: 'RM 98', unit: t('per month') })
      },
      {
        name: t('Growth plan'),
        audience: t('For growing businesses'),
        icon: 'chart-line',
        badge: t('Most Popular'),
        blurb: t('Great for growing businesses who want a professional image and improve their branding.'),
        price: 'RM 118',
        unit: t('per month, billed annually'),
        alt: t('or {price} {unit}', { price: 'RM 258', unit: t('per month') }),
        hit: true
      },
      {
        name: t('Custom plan'),
        audience: t('For custom builds'),
        icon: 'pen-ruler',
        blurb: t('Best suited for businesses who want more customisations to fit their exact requirements.'),
        price: t('Quote'),
        unit: t('then RM 118 per month')
      }
    ]);

    const rows = computed(() => [
      { label: t('Pages'), hint: t('How many pages your website can have, plus extra pages under them.'), cells: [{ type: 'text', value: t('1 main pages,\nno subpages') }, { type: 'text', value: t('7 main pages,\nunlimited subpages') }, { type: 'text', value: t('Unlimited'), accent: true }] },
      { label: t('Revisions'), hint: t('How many times we can still change the look and the wording.'), cells: [{ type: 'text', value: t('Until first launch') }, { type: 'text', value: t('Until first launch') }, { type: 'text', value: t('Unlimited'), accent: true }] },
      { label: t('Hosting, domain, SSL'), hint: t('We keep the site online, give it a web address, and add the lock in the browser.'), cells: [yes, yes, yes] },
      { label: t('CDN and anti-DDoS'), hint: t('Helps pages open faster, and helps the site stay up if someone overloads it.'), cells: [yes, yes, yes] },
      { label: t('Business email'), hint: t('Your own email using the website name, like hello@yourshop.com.'), cells: [{ type: 'text', value: '1' }, { type: 'text', value: '5' }, { type: 'text', value: '10' }] },
      { label: t('Google Analytics 4'), hint: t('A report that shows how many people visit your website.'), cells: [yes, yes, yes] },
      { label: t('Technical support'), hint: t('You can contact us if the website has a problem or you need help.'), cells: [yes, yes, yes] },
      { label: t('Backups'), hint: t('A saved copy of the website we can restore if something goes wrong.'), cells: [no, { type: 'text', value: t('Weekly') }, { type: 'text', value: t('Daily') }] },
      { label: t('MySQL database'), hint: t('Where the website stores things like products, forms and member details.'), cells: [no, no, yes] },
      { label: t('Source code repository'), hint: t('The files that make the website, kept so you can take them later.'), cells: [no, no, yes] }
    ]);

    const remarks = computed(() => [
      t('Web subscription plans have a minimum 12-month contract that starts after the project launch.'),
      t('Build time is about a week for the Startup plan, two to four weeks for the Growth plan, and set out in the project proposal for the Custom plan.')
    ]);

    return { t, columns, rows, remarks };
  }
};
</script>
